import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Button,
  Modal,
  IconButton,
  Menu,
  MenuItem,
  TextField
} from '@mui/material';
import * as XLSX from 'xlsx';
import { listLoans, deleteLoan, setReminder } from '../../api';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DownloadIcon from '@mui/icons-material/Download';

const formatDate = (date) => {
  if (!date || isNaN(new Date(date))) return 'N/A';
  return new Date(date).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const LoanTable = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loans, setLoans] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const rowRefs = useRef({});

  const fetchLoans = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listLoans();
      setLoans(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoans();
  }, []);

  useEffect(() => {
    if (id && rowRefs.current[id]) {
      rowRefs.current[id].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [id, loans]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this loan?')) {
      try {
        await deleteLoan(id);
        fetchLoans();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleView = (row) => {
    setViewData(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setViewData(null);
  };

  const handleReminderClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecordId(id);
  };

  const handleReminderClose = () => {
    setAnchorEl(null);
    setSelectedRecordId(null);
  };

  const handleSetReminder = async (days) => {
    try {
      await setReminder(selectedRecordId, 'loan', days);
      handleReminderClose();
      fetchLoans();
      alert(`Reminder set for ${days} day(s) before EMI date`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExport = () => {
    const exportData = loans.map(item => ({
      'Company Name': item.company_name,
      'Principal Amount': item.principal_amount,
      'Interest Rate': item.interest_rate,
      'Locations': item.locations?.map(loc => loc.covered_location).join(', ') || 'N/A',
      'Start Date': formatDate(item.start_date),
      'Maturity Date': formatDate(item.maturity_date),
      'EMI Date': formatDate(item.emi_date),
      'Interest Type': item.interest_type || 'N/A',
      'Tenure Frequency': item.tenure_frequency || 'N/A'
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Loans');
    XLSX.writeFile(workbook, 'loan_payments.xlsx');
  };

  const filteredLoans = loans.filter((loan) => {
    const search = searchTerm.toLowerCase();
    return (
      loan.company_name?.toLowerCase().includes(search) ||
      formatDate(loan.start_date).includes(search) ||
      formatDate(loan.maturity_date).includes(search)
    );
  });

  if (loading) return <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>;
  if (error) {
    return (
      <Box display="flex" justifyContent="center" p={3} alignItems="center">
        <Typography color="error" mr={2}>{error}</Typography>
        <Button variant="contained" onClick={fetchLoans} sx={{ backgroundColor: '#e0b256' }}>Retry</Button>
      </Box>
    );
  }

  return (
    <>
      <Paper elevation={0} sx={{ p: 2, backgroundColor: '#121212' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Loan Payments</Typography>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport} sx={{ backgroundColor: '#e0b256' }}>
            Export to Excel
          </Button>
        </Box>

        <TextField
          placeholder="Search by company or date"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          sx={{ mb: 2, input: { color: '#fff' } }}
          InputProps={{ style: { backgroundColor: '#1e1e1e', borderRadius: 4, color: '#fff' } }}
        />

        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#ccc' }}>Company</TableCell>
              <TableCell sx={{ color: '#ccc' }}>Principal</TableCell>
              <TableCell sx={{ color: '#ccc' }}>Rate</TableCell>
              <TableCell sx={{ color: '#ccc' }}>Locations</TableCell>
              <TableCell sx={{ color: '#ccc' }}>Start Date</TableCell>
              <TableCell sx={{ color: '#ccc' }}>Maturity Date</TableCell>
              <TableCell sx={{ color: '#ccc' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLoans.length > 0 ? (
              filteredLoans.map((row) => (
                <TableRow key={row._id} ref={(el) => (rowRefs.current[row._id] = el)} sx={{ '&:hover': { backgroundColor: '#333' } }}>
                  <TableCell sx={{ color: '#fff' }}>{row.company_name}</TableCell>
                  <TableCell sx={{ color: '#fff' }}>{row.principal_amount}</TableCell>
                  <TableCell sx={{ color: '#fff' }}>{row.interest_rate}</TableCell>
                  <TableCell sx={{ color: '#fff' }}>
                    {row.locations?.map(loc => loc.covered_location).join(', ') || 'N/A'}
                  </TableCell>
                  <TableCell sx={{ color: '#fff' }}>{formatDate(row.start_date)}</TableCell>
                  <TableCell sx={{ color: '#fff' }}>{formatDate(row.maturity_date)}</TableCell>
                  <TableCell>
                    <IconButton aria-label="View loan details" onClick={() => handleView(row)} sx={{ color: '#e0b256' }}>
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton aria-label="Edit loan" onClick={() => navigate(`/edit-loan/${row._id}`, { state: { data: row } })} sx={{ color: '#e0b256' }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton aria-label="Delete loan" onClick={() => handleDelete(row._id)} sx={{ color: '#e0b256' }}>
                      <DeleteIcon />
                    </IconButton>
                    <IconButton aria-label="Set reminder" onClick={(e) => handleReminderClick(e, row._id)} sx={{ color: '#e0b256' }}>
                      <NotificationsIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow><TableCell colSpan={7} sx={{ color: '#ccc' }}>No loans found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

     <Modal open={openModal} onClose={handleCloseModal}>
  <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: '#121212', p: 4, borderRadius: 2, minWidth: 400, maxWidth: 600, color: '#fff' }}>
    <Typography variant="h6" gutterBottom>Loan Details</Typography>
    {viewData && Object.entries(viewData)
      .filter(([key]) => !['_id', '__v'].includes(key))
      .map(([key, val]) => (
        <Typography key={key} variant="body2" sx={{ mb: 1.5 }}>
          <strong>{key.replace(/_/g, ' ').toUpperCase()}:</strong>
          {Array.isArray(val) ? (
            val.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {val.map((item, index) => (
                  <li key={index}>{item.covered_location || JSON.stringify(item) || 'N/A'}</li>
                ))}
              </ul>
            ) : 'N/A'
          ) : typeof val === 'object' && val !== null ? (
            Object.keys(val).length > 0 ? (
              <div>
                {Object.entries(val).map(([subKey, subVal]) => (
                  <Typography key={subKey} variant="body2" sx={{ ml: 2 }}>
                    {subKey.replace(/_/g, ' ').toUpperCase()}: {subVal || 'N/A'}
                  </Typography>
                ))}
              </div>
            ) : 'N/A'
          ) : (
            <span>{val || 'N/A'}</span>
          )}
        </Typography>
      ))}
    <Button fullWidth variant="contained" onClick={handleCloseModal} sx={{ mt: 2, backgroundColor: '#e0b256' }}>Close</Button>
  </Box>
</Modal>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleReminderClose} PaperProps={{ sx: { bgcolor: '#121212', color: '#fff', p: 2, width: 220 } }}>
        {[30, 15, 5, 1].map((days) => (
          <MenuItem key={days} onClick={() => handleSetReminder(days)} sx={{ '&:hover': { bgcolor: '#e0b256', color: '#000' } }}>
            {days} Day{days > 1 ? 's' : ''} Before
          </MenuItem>
        ))}
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>Custom Days</Typography>
          <input
            type="number"
            min="1"
            placeholder="Enter days"
            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #999', backgroundColor: '#1f1f1f', color: '#fff' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const days = parseInt(e.target.value);
                if (!isNaN(days) && days > 0) handleSetReminder(days);
                else alert('Please enter a valid number of days');
              }
            }}
          />
        </Box>
      </Menu>
    </>
  );
};

export default LoanTable;